import { createContext, useContext } from "react";

export interface ViewerUserWithToken extends User {
  token?: string | null;
}

// Notifications service supports different "profile" or identity types.
// For the most part, Duet uses the Firebase profile type.
export enum ProfileType {
  Coral = "Coral",
  Cyclone = "Cyclone",
  Firebase = "Firebase",
}

export enum NotificationSource {
  Coral = "Coral",
  Cyclone = "Cyclone",
}

export enum NotificationType {
  CoralRec = "CoralRec",
  CoralReply = "CoralReply",
  CoralCommentApproved = "CoralCommentApproved",
  CycloneReplyRec = "CycloneReplyRec",
  CycloneReply = "CycloneReply",
  CycloneFanPostRec = "CycloneFanPostRec",
  CycloneFanPostReply = "CycloneFanPostReply",
  CoralRecSummary = "CoralRecSummary",
  CycloneFPRecSummary = "CycloneFPRecSummary",
  CycloneReplyRecSummary = "CycloneReplyRecSummary",
  CycloneFanPostApproved = "CycloneFanPostApproved",
  CycloneReplyApproved = "CycloneReplyApproved",
}

export enum NotificationsFilter {
  All = "All",
  Unread = "Unread",
  Unseen = "Unseen",
}

export enum NotificationsSort {
  NewestFirst = "NewestFirst",
  OldestFirst = "OldestFirst",
}

export type ExternalUserProfile = {
  id: string;
  username: string;
  type: ProfileType;
  profileUrl?: string | null;
  avatarUrl?: string | null;
};

export type InPageNotificationSettings = {
  enabled: boolean;
  onCommentReply: boolean;
  onPostReply: boolean;
  onModeration: boolean;
  onRec: boolean;
};

export type User = {
  id: string;
  profiles?: ExternalUserProfile[] | null;
  lastViewed?: Date | null;
  inPageNotificationSettings?: InPageNotificationSettings | null;
};

export type CycloneReply = {
  id: string;
  url: string;
  snippet: string;
};

export enum RootAncestorType {
  CycloneFanPost = "CycloneFanPost",
  CycloneExternalRoot = "CycloneExternalRoot",
}

export type CycloneFanPost = {
  title: string;
  url: string;
  snippet: string;
  shortId: string;
  id: string;
  type: RootAncestorType;
};

export type CycloneExternalRoot = {
  shortId: string;
  friendlyId: string;
  urls: string[];
  type: RootAncestorType;
  id: string;
};

export type CycloneRootAncestor = CycloneFanPost | CycloneExternalRoot;

export type CycloneCommunity = {
  id: string;
  name: string;
  logo: string;
};

export type CoralComment = {
  id: string;
  snippet: string;
  url: string;
  storyId: string;
};

export type CoralStory = {
  id: string;
  title: string;
  url: string;
};

export type CoralSite = {
  id: string;
  name: string;
};

export type Notification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  seen: boolean;
  read: boolean;
};

export type CoralReplyNotification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  seen: boolean;
  read: boolean;

  reply: CoralComment;
  from: User;
  to: User;
  comment: CoralComment;
  story: CoralStory;
  site: CoralSite;
};

export type CoralRecNotification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  seen: boolean;
  read: boolean;

  comment: CoralComment;
  from: User;
  to: User;
  story: CoralStory;
  site: CoralSite;
};

export type CoralCommentWasApprovedNotification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  seen: boolean;
  read: boolean;

  to: User;
  story: CoralStory;
  site: CoralSite;
  comment: CoralComment;
};

export type CycloneFanPostWasApprovedNotification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  seen: boolean;
  read: boolean;

  to: User;
  fanPost: CycloneFanPost;
  community: CycloneCommunity;
  childIds?: string[];
};

export type CycloneReplyWasApprovedNotification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  seen: boolean;
  read: boolean;

  to: User;
  reply: CycloneReply;
  community: CycloneCommunity;
  childIds?: string[];
};

export type CycloneFanPostReplyNotification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  seen: boolean;
  read: boolean;

  from: User;
  to: User;
  fanPost: CycloneFanPost;
  reply: CycloneReply;
  community: CycloneCommunity;
};

export type CycloneReplyNotification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  seen: boolean;
  read: boolean;

  from: User;
  to: User;
  reply: CycloneReply;
  parent: CycloneReply;
  fanPost: CycloneFanPost;
  community: CycloneCommunity;
  rootAncestor?: CycloneRootAncestor;
};

export type CycloneFanPostRecNotification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  seen: boolean;
  read: boolean;

  from: User;
  to: User;
  fanPost: CycloneFanPost;
  community: CycloneCommunity;
};

export type CycloneReplyRecNotification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  seen: boolean;
  read: boolean;

  from: User;
  to: User;
  fanPost: CycloneFanPost;
  reply: CycloneReply;
  community: CycloneCommunity;
  rootAncestor?: CycloneRootAncestor;
};

export type RecSummaryNotification = {
  id: string;
  type: NotificationType;
  source: NotificationSource;
  createdAt: string;
  updatedAt: string;
  seen: boolean;
  read: boolean;

  to: User;
  recs: Notification[];
};

export type CoralRecSummaryNotification = RecSummaryNotification & {
  story: CoralStory;
  site: CoralSite;
  comment: CoralComment;
};

export type CycloneFanPostRecSummaryNotification = RecSummaryNotification & {
  fanPost: CycloneFanPost;
  community: CycloneCommunity;
};

export type CycloneReplyRecSummaryNotification = RecSummaryNotification & {
  reply: CycloneReply;
  community: CycloneCommunity;
  rootAncestor?: CycloneRootAncestor;
};

export type AnyCycloneNotification =
  | CycloneFanPostReplyNotification
  | CycloneReplyNotification
  | CycloneFanPostRecNotification
  | CycloneReplyRecNotification
  | CycloneFanPostRecSummaryNotification
  | CycloneReplyRecSummaryNotification
  | CycloneFanPostWasApprovedNotification
  | CycloneReplyWasApprovedNotification;

export type AnyCoralNotification =
  | CoralReplyNotification
  | CoralRecNotification
  | CoralRecSummaryNotification
  | CoralCommentWasApprovedNotification;

export type AnyRecSummaryNotification =
  | CycloneFanPostRecSummaryNotification
  | CycloneReplyRecSummaryNotification
  | CoralRecSummaryNotification;

export type AnyRecNotification =
  | CycloneReplyRecNotification
  | CycloneFanPostRecNotification
  | CoralRecNotification;

export type AnyReplyNotification =
  | CycloneFanPostReplyNotification
  | CycloneReplyNotification
  | CoralReplyNotification;

export type AnyNotification = AnyCycloneNotification | AnyCoralNotification;

export interface NotificationsStatus {
  unseen: number;
  unread: number;
  lastViewed: Date;
}

export const DEFAULT_NOTIFICATIONS_STATUS: NotificationsStatus = {
  unseen: 0,
  unread: 0,
  lastViewed: new Date(0),
};

export interface UserStore {
  viewer: ViewerUserWithToken | null;
  viewerLoading: boolean;
  setViewer: (viewer: ViewerUserWithToken | null) => void;
  loadViewer: () => Promise<void>;
  setLastViewed: (lastViewed: Date) => Promise<void>;
}

export interface NotificationsStore {
  lookupById: any;
  userStore: UserStore | null;
  notificationsStatus: NotificationsStatus;
  pollIntervalId: NodeJS.Timeout | null;
  unseenNotificationsDisplayCount: number | null;
  markRead: (params: {
    ids?: string[];
    all?: boolean;
    updateStore?: boolean;
  }) => Promise<void>;
  insert: (notification: AnyNotification) => AnyNotification | null | undefined;
  startPollingNotificationsStatus: (intervalMs?: number) => void;
  stopPollingNotificationsStatus: () => void;
}

export interface NotificationsListStore {
  notifications: AnyNotification[];
  hasNext: boolean;
  cursor: string | null;
  loading: boolean;
  loadNotificationsForUser: (params?: {
    cursor?: string;
    filter?: NotificationsFilter;
    count?: number;
  }) => Promise<void>;
  refreshNotifications: (params?: {
    cursor?: string;
    sort?: NotificationsSort;
    filter?: NotificationsFilter;
  }) => Promise<void>;
  setHasNext: (hasNext: boolean) => void;
  setCursor: (cursor: string | null) => void;
}

export interface AllNotificationsStore extends NotificationsListStore {
  visibleNotifications: AnyNotification[];
}

export interface UnreadNotificationsStore extends NotificationsListStore {
  visibleNotifications: AnyNotification[];
}

export interface NotificationsRootStore {
  userStore: UserStore;
  notificationsStore: NotificationsStore;
  allNotificationsStore: AllNotificationsStore;
  unreadNotificationsStore: UnreadNotificationsStore;
}

export interface NotificationsContextType {
  RootStore: NotificationsRootStore | null;
}

export interface NotificationsDrawerContextType {
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
}

const NotificationsContext = createContext<NotificationsContextType>({
  RootStore: null,
});

export const NotificationsDrawerContext =
  createContext<NotificationsDrawerContextType | null>(null);

/**
 * Hook to access notifications context
 */
export function useNotificationsStore(): NotificationsContextType {
  const context = useContext(NotificationsContext);
  return context;
}

export function useOptionalNotificationsDrawerContext() {
  return useContext(NotificationsDrawerContext);
}

export default NotificationsContext;
